/**
 * Quick add — income or expense.
 *
 * The whole design target is three taps: amount → category → save. Everything
 * that can be defaulted is: today's date, the last-used account, the most-used
 * categories first. Anything that cannot be defaulted sits behind "още".
 *
 * If entering an expense takes longer than about ten seconds on a phone, it
 * does not get entered, and a ledger with holes in it is worse than no ledger.
 */
import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/misc';
import { ResponsiveDialog } from '@/components/ui/dialog';
import { AmountInput, AmountPresets } from '@/components/AmountInput';
import { AccountPicker, isCompleteRef } from '@/components/AccountPicker';
import { colorClasses } from '@/lib/colors';
import { todayIso, toCanonicalAmount } from '@/lib/format';
import { useCategories, useCreateEntry, useCurrencies, useVoidTransaction } from '@/hooks/useApi';
import { useLastUsed } from '@/hooks/useLastUsed';
import type { AccountRef, CategoryKind } from '@/lib/types';

export function QuickAddSheet({
  open,
  onOpenChange,
  defaultFundId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultFundId?: number;
}): JSX.Element {
  const [kind, setKind] = useState<CategoryKind>('EXPENSE');
  const [amountRaw, setAmountRaw] = useState('');
  const [amountMinor, setAmountMinor] = useState<number | null>(null);
  const [account, setAccount] = useState<Partial<AccountRef>>({});
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [date, setDate] = useState(todayIso());
  const [description, setDescription] = useState('');
  const [note, setNote] = useState('');
  const [showMore, setShowMore] = useState(false);

  const { lastAccount, remember } = useLastUsed();
  const { data: currencies = [] } = useCurrencies();
  const { data: categories = [] } = useCategories({ kind, fundId: account.fundId });
  const createEntry = useCreateEntry();
  const voidTx = useVoidTransaction();

  const decimals = currencies.find((c) => c.code === account.currency)?.decimals ?? 2;

  // Reset on each open so a half-finished entry never leaks into the next one.
  useEffect(() => {
    if (!open) return;
    setKind('EXPENSE');
    setAmountRaw('');
    setAmountMinor(null);
    setCategoryId(null);
    setDate(todayIso());
    setDescription('');
    setNote('');
    setShowMore(false);
    setAccount(
      defaultFundId
        ? { ...lastAccount, fundId: defaultFundId }
        : (lastAccount ?? {}),
    );
  }, [open, defaultFundId, lastAccount]);

  // Most-used first — that ordering is most of what makes this fast.
  const sortedCategories = useMemo(
    () =>
      [...categories].sort(
        (a, b) => (b.usageCount ?? 0) - (a.usageCount ?? 0) || a.name.localeCompare(b.name, 'bg'),
      ),
    [categories],
  );

  const canSave = amountMinor != null && amountMinor > 0 && isCompleteRef(account);

  const handleSave = async () => {
    if (!canSave || !isCompleteRef(account) || amountMinor == null) return;

    try {
      const created = await createEntry.mutateAsync({
        kind,
        date,
        amount: toCanonicalAmount(amountMinor, decimals),
        account,
        categoryId,
        description: description || undefined,
        note: note || undefined,
      });

      remember(account);
      onOpenChange(false);

      // Undo instead of a confirm dialog: confirmation taxes every correct
      // entry to guard against the rare wrong one. Undo does the opposite.
      toast.success(kind === 'INCOME' ? 'Приходът е записан' : 'Разходът е записан', {
        duration: 10_000,
        action: {
          label: 'Отмени',
          onClick: () => {
            voidTx.mutate(
              { id: created.id, reason: 'Отменено веднага след въвеждане' },
              {
                onSuccess: () => toast.success('Операцията е отменена'),
                onError: (e) => toast.error((e as Error).message),
              },
            );
          },
        },
      });
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Нова операция"
      className="sm:max-w-md"
    >
      <div className="space-y-4">
        {/* 1 — direction */}
        <div className="grid grid-cols-2 gap-2">
          {(
            [
              ['EXPENSE', 'Разход', 'data-[on=true]:bg-expense/15 data-[on=true]:text-expense data-[on=true]:border-expense/30'],
              ['INCOME', 'Приход', 'data-[on=true]:bg-income/15 data-[on=true]:text-income data-[on=true]:border-income/30'],
            ] as const
          ).map(([value, label, activeClass]) => (
            <button
              key={value}
              type="button"
              data-on={kind === value}
              onClick={() => {
                setKind(value);
                setCategoryId(null);
              }}
              className={cn(
                'h-11 rounded-md border text-sm font-medium text-muted-foreground transition-colors',
                activeClass,
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {/* 2 — amount */}
        <div className="space-y-2">
          <AmountInput
            id="qa-amount"
            size="hero"
            value={amountRaw}
            onChange={setAmountRaw}
            onValueChange={setAmountMinor}
            currency={account.currency ?? 'EUR'}
            decimals={decimals}
            autoFocus
          />
          <AmountPresets
            presets={[500, 1000, 2000, 5000, 10000]}
            decimals={decimals}
            onPick={setAmountRaw}
          />
        </div>

        {/* 3 — where */}
        <AccountPicker value={account} onChange={setAccount} label="Каса и място" idPrefix="qa" />

        {/* 4 — category */}
        <div className="space-y-2">
          <Label>Категория</Label>
          <div className="flex flex-wrap gap-2">
            {sortedCategories.slice(0, 10).map((c) => {
              const colors = colorClasses(c.color);
              const active = categoryId === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCategoryId(active ? null : c.id)}
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-sm transition-colors',
                    active
                      ? cn(colors.soft, colors.text, colors.border, 'font-medium')
                      : 'text-muted-foreground hover:bg-accent',
                  )}
                >
                  {c.name}
                </button>
              );
            })}
            {sortedCategories.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Няма категории за тази каса. Добави от Настройки.
              </p>
            ) : null}
          </div>
        </div>

        {/* Everything else, folded away */}
        <button
          type="button"
          onClick={() => setShowMore((v) => !v)}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronDown className={cn('h-4 w-4 transition-transform', showMore && 'rotate-180')} />
          {showMore ? 'По-малко' : 'Още — дата, описание, бележка'}
        </button>

        {showMore ? (
          <div className="space-y-3 rounded-md border p-3">
            <div className="space-y-1.5">
              <Label htmlFor="qa-date">Дата</Label>
              <Input
                id="qa-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                max={todayIso()}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="qa-desc">Описание</Label>
              <Input
                id="qa-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="напр. вноска ап. 12"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="qa-note">Бележка</Label>
              <Textarea
                id="qa-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
              />
            </div>
          </div>
        ) : null}

        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
            Откажи
          </Button>
          <Button
            className="flex-1"
            disabled={!canSave || createEntry.isPending}
            onClick={() => void handleSave()}
          >
            {createEntry.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Запиши
          </Button>
        </div>
      </div>
    </ResponsiveDialog>
  );
}
